import Database from '@tauri-apps/plugin-sql';
import { createHash } from 'crypto';
import type { GetAllRequestBody } from '../lib/types';

export type PendingChangeStatus = 'pending' | 'synced' | 'failed';
export type PendingChangeAction = 'add' | 'update' | 'delete';

/**
 * Sanitizes a string for use as a SQL table name suffix.
 * Normalizes the input by replacing disallowed characters with underscores.
 * Handles common user ID formats like UUIDs and emails.
 */
function sanitizeTableSuffix(suffix: string): string {
  if (!suffix) {
    throw new Error('Table suffix cannot be empty');
  }

  // Replace disallowed characters with underscores
  let sanitized = suffix.replace(/[^a-zA-Z0-9_]/g, '_');

  // Collapse consecutive underscores into one
  sanitized = sanitized.replace(/_+/g, '_');

  // Trim leading and trailing underscores
  sanitized = sanitized.replace(/^_+|_+$/g, '');

  // Ensure result isn't empty after normalization
  if (!sanitized) {
    throw new Error(
      `Table suffix "${suffix}" resulted in empty string after normalization`
    );
  }

  // Ensure it doesn't start with a number (SQL identifier rule)
  if (/^[0-9]/.test(sanitized)) {
    return `u_${sanitized}`;
  }

  return sanitized;
}

/**
 * Builds a user-specific table name safely.
 */
function buildUserTableName(tableName: string, userId?: string): string {
  if (!userId) {
    return tableName;
  }
  return `${tableName}_${sanitizeTableSuffix(userId)}`;
}

export interface PendingChange {
  id: string;
  userId?: string | null;
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  action: PendingChangeAction;
  status: PendingChangeStatus;
  createdAt: number;
  attempts: number;
  lastError?: string | null;
}

// Initialize or open the database
async function getDatabase(): Promise<Database> {
  const db = await Database.load('sqlite:readcloud.sqlite');
  return db;
}

// ---------------------------------------------------------------------------
// Write queue — serializes all SQLite write operations so that concurrent
// callers (e.g. parallel get/all refreshes) never attempt overlapping writes
// on the shared connection pool.
// ---------------------------------------------------------------------------
let _writeQueue: Promise<void> = Promise.resolve();

function enqueueWrite<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    _writeQueue = _writeQueue.then(() => fn().then(resolve, reject));
  });
}

// ---------------------------------------------------------------------------
// Table creation — CREATE TABLE is a write, so it must go through the queue.
// Cache created tables in-memory so repeated calls don't re-enter the queue
// just to run an idempotent CREATE TABLE IF NOT EXISTS. The pending map
// collapses concurrent first-time calls onto a single queued creation.
// ---------------------------------------------------------------------------
const _existingTables = new Set<string>();
const _pendingTableCreates = new Map<string, Promise<void>>();

function ensureTableExists(tableName: string): Promise<void> {
  if (_existingTables.has(tableName)) return Promise.resolve();
  let pending = _pendingTableCreates.get(tableName);
  if (!pending) {
    pending = enqueueWrite(async () => {
      const db = await getDatabase();
      await db.execute(`
        CREATE TABLE IF NOT EXISTS ${tableName} (
          id TEXT PRIMARY KEY,
          data TEXT
        )
      `);
      _existingTables.add(tableName);
    }).finally(() => {
      _pendingTableCreates.delete(tableName);
    });
    _pendingTableCreates.set(tableName, pending);
  }
  return pending;
}

let _pendingChangesReady: Promise<void> | null = null;

function ensurePendingChangesTableExists(): Promise<void> {
  if (_existingTables.has('pending_changes')) return Promise.resolve();
  if (!_pendingChangesReady) {
    _pendingChangesReady = enqueueWrite(async () => {
      const db = await getDatabase();
      await db.execute(`
        CREATE TABLE IF NOT EXISTS pending_changes (
          id TEXT PRIMARY KEY,
          userId TEXT,
          url TEXT NOT NULL,
          method TEXT NOT NULL,
          headers TEXT,
          body TEXT,
          entityType TEXT,
          entityId TEXT,
          action TEXT NOT NULL,
          status TEXT NOT NULL,
          createdAt INTEGER NOT NULL,
          attempts INTEGER NOT NULL DEFAULT 0,
          lastError TEXT
        )
      `);
      await db.execute(
        `CREATE INDEX IF NOT EXISTS idx_pending_changes_status_createdAt ON pending_changes(status, createdAt)`
      );
      await db.execute(
        `CREATE INDEX IF NOT EXISTS idx_pending_changes_userId ON pending_changes(userId)`
      );
      _existingTables.add('pending_changes');
    });
  }
  return _pendingChangesReady;
}

// Save a collection to its own table. Because tauri-plugin-sql (sqlx)
// maintains a connection pool, multi-statement `BEGIN/COMMIT` can't be used
// to make this atomic across execute() calls. Instead we rely on the JS
// write queue to serialize callers, then do DELETE + INSERT OR REPLACE so
// that any race (or a mid-write crash) tolerates duplicates on the next run.
export async function saveCollection(
  tableName: string,
  collection: any[],
  userId?: string
): Promise<void> {
  const userSpecificTableName = buildUserTableName(tableName, userId);
  await ensureTableExists(userSpecificTableName);

  return enqueueWrite(async () => {
    const db = await getDatabase();

    console.log(
      `Saving collection to table: ${userSpecificTableName} with ${collection.length} items`
    );

    await db.execute(`DELETE FROM ${userSpecificTableName}`);

    for (const item of collection) {
      const id =
        item.id ||
        createHash('sha256').update(JSON.stringify(item)).digest('hex');

      await db.execute(
        `INSERT OR REPLACE INTO ${userSpecificTableName} (id, data) VALUES (?, ?)`,
        [id, JSON.stringify(item)]
      );
    }

    console.log(`Saved collection "${userSpecificTableName}" successfully.`);
  });
}

export async function upsertItem(
  tableName: string,
  item: any,
  userId?: string
): Promise<void> {
  const userSpecificTableName = buildUserTableName(tableName, userId);
  await ensureTableExists(userSpecificTableName);

  return enqueueWrite(async () => {
    const db = await getDatabase();

    const id =
      item?.id || createHash('sha256').update(JSON.stringify(item)).digest('hex');

    await db.execute(
      `INSERT OR REPLACE INTO ${userSpecificTableName} (id, data) VALUES (?, ?)`,
      [id, JSON.stringify(item)]
    );
  });
}

/**
 * Apply a batch of /update/app_state PUT items to the local cache.
 *
 * The endpoint accepts an array of items keyed logically by (tags.realm,
 * tags.bookId), not by a top-level `id`. Rows written by /get/all's
 * saveCollection carry the server-assigned `id`, so we look up the existing
 * row by realm+bookId and preserve its `id` when merging. Items with no
 * matching cache row get a temp id; the next successful /get/all will clear
 * it (saveCollection does a DELETE before re-inserting from the server).
 *
 * Writes to the camelCase `appStates_<userId>` table — the same one
 * saveCollection populates and reconstructResponse reads — so optimistic
 * updates remain visible across an offline open/reopen of the reader.
 */
export async function upsertAppStateBatch(
  items: any[],
  userId?: string
): Promise<void> {
  if (!Array.isArray(items) || items.length === 0) return;

  const userSpecificTableName = buildUserTableName('appStates', userId);
  await ensureTableExists(userSpecificTableName);

  return enqueueWrite(async () => {
    const db = await getDatabase();

    const existing = await db.select<Array<{ id: string; data: string }>>(
      `SELECT id, data FROM ${userSpecificTableName}`
    );
    const indexByKey = new Map<string, { id: string; row: any }>();
    for (const r of existing) {
      try {
        const parsed = JSON.parse(r.data);
        const key = `${parsed?.tags?.realm ?? ''}:${parsed?.tags?.bookId ?? ''}`;
        indexByKey.set(key, { id: r.id, row: parsed });
      } catch {
        // skip unparseable rows
      }
    }

    for (const item of items) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const key = `${item?.tags?.realm ?? ''}:${item?.tags?.bookId ?? ''}`;
      const match = indexByKey.get(key);
      const merged = match
        ? { ...match.row, ...item, id: match.id }
        : {
            ...item,
            id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          };
      await db.execute(
        `INSERT OR REPLACE INTO ${userSpecificTableName} (id, data) VALUES (?, ?)`,
        [merged.id, JSON.stringify(merged)]
      );
    }
  });
}

export async function deleteItem(
  tableName: string,
  id: string,
  userId?: string
): Promise<void> {
  const userSpecificTableName = buildUserTableName(tableName, userId);
  await ensureTableExists(userSpecificTableName);

  return enqueueWrite(async () => {
    const db = await getDatabase();
    await db.execute(`DELETE FROM ${userSpecificTableName} WHERE id = ?`, [id]);
  });
}

export async function replaceEntityId(
  tableName: string,
  oldId: string,
  newId: string,
  userId?: string
): Promise<boolean> {
  const userSpecificTableName = buildUserTableName(tableName, userId);
  await ensureTableExists(userSpecificTableName);

  return enqueueWrite(async () => {
    const db = await getDatabase();

    const rows = await db.select<Array<{ data: string }>>(
      `SELECT data FROM ${userSpecificTableName} WHERE id = ?`,
      [oldId]
    );

    if (rows.length === 0) {
      return false;
    }

    const item = JSON.parse(rows[0].data);
    item.id = newId;

    await db.execute(`DELETE FROM ${userSpecificTableName} WHERE id = ?`, [oldId]);
    await db.execute(
      `INSERT OR REPLACE INTO ${userSpecificTableName} (id, data) VALUES (?, ?)`,
      [newId, JSON.stringify(item)]
    );

    return true;
  });
}

// Retrieve all items from a table
export async function retrieveCollection(
  tableName: string,
  userId?: string
): Promise<any[]> {
  // Include user context in table name to prevent data mixing
  const userSpecificTableName = buildUserTableName(tableName, userId);

  console.log(`Retrieving collection from table: ${userSpecificTableName}`);

  await ensureTableExists(userSpecificTableName);
  const db = await getDatabase();

  const result = await db.select<Array<{ data: string }>>(
    `SELECT data FROM ${userSpecificTableName}`
  );

  console.log(
    `Retrieved ${result.length} items from table: ${userSpecificTableName}`
  );

  return result.map((row) => JSON.parse(row.data));
}

function stablePendingId(input: {
  url: string;
  method: string;
  body?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  action: PendingChangeAction;
  userId?: string | null;
}): string {
  return createHash('sha256')
    .update(
      JSON.stringify({
        url: input.url,
        method: input.method,
        body: input.body || null,
        entityType: input.entityType || null,
        entityId: input.entityId || null,
        action: input.action,
        userId: input.userId || null,
      })
    )
    .digest('hex');
}

export async function enqueuePendingChange(
  change: Omit<PendingChange, 'id' | 'status' | 'createdAt' | 'attempts'> & {
    userId?: string | null;
  }
): Promise<PendingChange> {
  await ensurePendingChangesTableExists();

  return enqueueWrite(async () => {
    const db = await getDatabase();

    const createdAt = Date.now();
    const body = change.body ?? null;
    const entityType = change.entityType ?? null;
    const entityId = change.entityId ?? null;
    const userId = change.userId ?? null;

    const id = stablePendingId({
      url: change.url,
      method: change.method,
      body,
      entityType,
      entityId,
      action: change.action,
      userId,
    });

    const record: PendingChange = {
      id,
      userId,
      url: change.url,
      method: change.method,
      headers: change.headers,
      body,
      entityType,
      entityId,
      action: change.action,
      status: 'pending',
      createdAt,
      attempts: 0,
      lastError: null,
    };

    await db.execute(
      `
      INSERT OR REPLACE INTO pending_changes
        (id, userId, url, method, headers, body, entityType, entityId, action, status, createdAt, attempts, lastError)
      VALUES
        (?,  ?,     ?,   ?,      ?,       ?,    ?,         ?,       ?,      ?,      ?,        ?,        ?)
    `,
      [
        record.id,
        record.userId,
        record.url,
        record.method,
        record.headers ? JSON.stringify(record.headers) : null,
        record.body,
        record.entityType,
        record.entityId,
        record.action,
        record.status,
        record.createdAt,
        record.attempts,
        record.lastError,
      ]
    );

    return record;
  });
}

export async function listPendingChanges(params?: {
  userId?: string | null;
  status?: PendingChangeStatus;
  limit?: number;
}): Promise<PendingChange[]> {
  await ensurePendingChangesTableExists();
  const db = await getDatabase();

  const where: string[] = [];
  const values: any[] = [];

  if (params?.status) {
    where.push(`status = ?`);
    values.push(params.status);
  }
  if (typeof params?.userId !== 'undefined') {
    // In SQL, `column = NULL` is always false; use IS NULL for null comparisons
    if (params.userId === null) {
      where.push(`userId IS NULL`);
    } else {
      where.push(`userId = ?`);
      values.push(params.userId);
    }
  }

  const whereSql = where.length ? `WHERE ${where.join(' AND ')}` : '';
  const limitSql = params?.limit ? `LIMIT ${params.limit}` : '';

  const rows = await db.select<
    Array<{
      id: string;
      userId: string | null;
      url: string;
      method: string;
      headers: string | null;
      body: string | null;
      entityType: string | null;
      entityId: string | null;
      action: PendingChangeAction;
      status: PendingChangeStatus;
      createdAt: number;
      attempts: number;
      lastError: string | null;
    }>
  >(
    `SELECT id, userId, url, method, headers, body, entityType, entityId, action, status, createdAt, attempts, lastError
     FROM pending_changes
     ${whereSql}
     ORDER BY createdAt ASC
     ${limitSql}`,
    values
  );

  return rows.map((r) => ({
    id: r.id,
    userId: r.userId,
    url: r.url,
    method: r.method,
    headers: r.headers ? (JSON.parse(r.headers) as Record<string, string>) : {},
    body: r.body,
    entityType: r.entityType,
    entityId: r.entityId,
    action: r.action,
    status: r.status,
    createdAt: r.createdAt,
    attempts: r.attempts,
    lastError: r.lastError,
  }));
}

export async function markPendingChangeSynced(id: string): Promise<void> {
  await ensurePendingChangesTableExists();
  return enqueueWrite(async () => {
    const db = await getDatabase();
    await db.execute(
      `UPDATE pending_changes SET status = 'synced', lastError = NULL WHERE id = ?`,
      [id]
    );
  });
}

export async function markPendingChangeFailed(
  id: string,
  error: string
): Promise<void> {
  await ensurePendingChangesTableExists();
  return enqueueWrite(async () => {
    const db = await getDatabase();
    await db.execute(
      `UPDATE pending_changes SET status = 'failed', lastError = ?, attempts = attempts + 1 WHERE id = ?`,
      [error, id]
    );
  });
}

// Reconstructing the response from the database
export async function reconstructResponse(
  requestBody: GetAllRequestBody,
  userId?: string
): Promise<any> {
  const result: Record<'result', any> = { result: {} };

  if (typeof requestBody !== 'object' || requestBody === null) {
    console.error('Invalid request body format. Expected an object.');
    return result;
  }

  for (const [key, value] of Object.entries(requestBody)) {
    if (value && value.get === true) {
      const tableName = key.replace('Opts', 's');
      try {
        const data = await retrieveCollection(tableName, userId);
        // Always include requested collections, even when empty — this
        // fabricated response must be shape-faithful to the real API.
        // Omitting the key hands thunks `undefined` where they expect an
        // array (e.g. bookmarks on a fresh install crashing the reader).
        result.result[tableName] = data;
      } catch (error) {
        console.error(
          `Failed to retrieve data for table "${tableName}":`,
          error
        );
      }
    }
  }

  return result;
}

// Clear all tables in the database (used during logout)
export async function clearAllTables(): Promise<void> {
  await ensurePendingChangesTableExists();

  return enqueueWrite(async () => {
    try {
      const db = await getDatabase();

      // List of all possible table names
      const tableNames = [
        'annotations',
        'clouds',
        'books',
        'comments',
        'bookmarks',
        'users',
        'schoolClouds',
        'appStates',
        // Historical orphan: older builds wrote PUT /update/app_state bodies
        // (batch arrays) into snake_case `app_states_<userId>` tables that no
        // read path consults. Include here so logout cleans up the bloat.
        'app_states',
      ];

      for (const tableName of tableNames) {
        try {
          // Clear the base table
          await db.execute(`DELETE FROM ${tableName}`);
          console.log(`Cleared table: ${tableName}`);

          // Also clear any user-specific tables (with _userId suffix)
          // This is a fallback to ensure all user data is cleared
          const result = await db.select<Array<{ name: string }>>(
            "SELECT name FROM sqlite_master WHERE type='table' AND name LIKE ?",
            [`${tableName}_%`]
          );

          for (const table of result) {
            await db.execute(`DELETE FROM ${table.name}`);
            console.log(`Cleared user-specific table: ${table.name}`);
          }
        } catch (error) {
          // Table might not exist, which is fine
          console.log(`Table ${tableName} doesn't exist or is already empty`);
        }
      }

      console.log('All database tables cleared successfully');

      try {
        await db.execute(`DELETE FROM pending_changes`);
        console.log('Cleared table: pending_changes');
      } catch (error) {
        console.log(`Table pending_changes doesn't exist or is already empty`);
      }
    } catch (error) {
      console.error('Error clearing database tables:', error);
    }
  });
}
